import React from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import type { DocumentListItem } from '@hooks/useDocumentsQuery';
import type { TFunction } from 'i18next';
import { Share2, Tag, Trash2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { TooltipIconButton } from '@/components/ui/tooltip-icon-button';
import { DATE_FORMAT, formatDateSafe, formatFileSize, getBestDate } from '@utils/dateUtils';
import { getCategoryLabel, getFileSize } from './documentFilters';

interface ColumnActions {
  t: TFunction;
  language?: string;
  canSort?: boolean;
  onShare: () => void;
  onEditTags?: (doc: DocumentListItem) => void;
  onDelete?: (doc: DocumentListItem) => void;
  canDelete?: (doc: DocumentListItem) => boolean;
}

/** Stops a row-level click when the control inside it is the real target. */
const swallow = (event: React.MouseEvent) => event.stopPropagation();

export function buildDocumentColumns({
  t,
  language,
  canSort = false,
  onShare,
  onEditTags,
  onDelete,
  canDelete,
}: ColumnActions): ColumnDef<DocumentListItem, unknown>[] {
  return [
    {
      id: 'select',
      size: 44,
      enableSorting: false,
      header: ({ table }) => (
        <span onClick={swallow} className="flex">
          <Checkbox
            checked={
              table.getIsAllRowsSelected() || (table.getIsSomeRowsSelected() && 'indeterminate')
            }
            onCheckedChange={(value) => table.toggleAllRowsSelected(!!value)}
            aria-label={t('common.selectAll')}
          />
        </span>
      ),
      cell: ({ row }) => (
        <span onClick={swallow} className="flex">
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            aria-label={row.original.title}
          />
        </span>
      ),
    },
    {
      id: 'title',
      accessorFn: (doc) => doc.title,
      header: () => t('documents.columns.title'),
      enableSorting: canSort,
      sortDescFirst: true,
      cell: ({ row }) => {
        const doc = row.original;
        return (
          <div className="min-w-0 max-w-xl">
            <p className="truncate text-sm font-medium">{doc.title}</p>
            {(doc.summary || doc.description) && (
              <p className="mt-0.5 line-clamp-1 text-xs text-muted-foreground">
                {doc.summary || doc.description}
              </p>
            )}
            {(doc.tags?.length > 0 || doc.analysisCount > 0) && (
              <div className="mt-1.5 flex flex-wrap items-center gap-1">
                {doc.analysisCount > 0 && (
                  <Badge variant="secondary" className="font-normal">
                    {t('documents.deepAnalyzed')}
                  </Badge>
                )}
                {doc.tags?.slice(0, 3).map((tag: string) => (
                  <Badge key={tag} variant="outline" className="font-normal">
                    {tag}
                  </Badge>
                ))}
                {doc.tags?.length > 3 && (
                  <span className="text-xs text-muted-foreground">+{doc.tags.length - 3}</span>
                )}
              </div>
            )}
          </div>
        );
      },
    },
    {
      id: 'category',
      accessorFn: (doc) => getCategoryLabel(doc),
      header: () => t('documents.columns.category'),
      enableSorting: canSort,
      sortDescFirst: true,
      cell: ({ row }) => (
        <Badge variant="outline" className="font-normal">
          {getCategoryLabel(row.original) || t('documents.generalCategory')}
        </Badge>
      ),
    },
    {
      id: 'size',
      accessorFn: (doc) => getFileSize(doc),
      header: () => t('documents.columns.size'),
      enableSorting: canSort,
      sortDescFirst: true,
      cell: ({ row }) => (
        <span className="whitespace-nowrap text-sm revy-figure text-muted-foreground">
          {formatFileSize(getFileSize(row.original))}
        </span>
      ),
    },
    {
      id: 'updatedAt',
      accessorFn: (doc) => getBestDate(doc) ?? '',
      header: () => t('documents.columns.updatedAt'),
      enableSorting: canSort,
      sortDescFirst: true,
      cell: ({ row }) => (
        <span className="whitespace-nowrap text-sm text-muted-foreground">
          {formatDateSafe(getBestDate(row.original), DATE_FORMAT, { t, language })}
        </span>
      ),
    },
    {
      id: 'actions',
      size: 132,
      enableSorting: false,
      header: () => <span className="sr-only">{t('common.actions')}</span>,
      cell: ({ row }) => (
        <div onClick={swallow} className="flex justify-end gap-0.5">
          <TooltipIconButton
            size="icon-sm"
            label={t('common.share')}
            icon={<Share2 strokeWidth={1.8} />}
            onClick={onShare}
          />
          {onEditTags && (
            <TooltipIconButton
              size="icon-sm"
              label={t('chat.editTags')}
              icon={<Tag strokeWidth={1.8} />}
              onClick={() => onEditTags(row.original)}
            />
          )}
          {onDelete && (!canDelete || canDelete(row.original)) && (
            <TooltipIconButton
              size="icon-sm"
              label={t('common.delete')}
              icon={<Trash2 strokeWidth={1.8} />}
              onClick={() => onDelete(row.original)}
              className="text-destructive hover:bg-destructive/10 hover:text-destructive"
            />
          )}
        </div>
      ),
    },
  ];
}
